export type PenaltyExposureProgressStatus = "queued" | "active" | "complete" | "error";

export interface PenaltyExposureProgressStep {
  stepId: string;
  label: string;
  status: PenaltyExposureProgressStatus;
}

export interface ComplianceProgressEvent {
  stepId: string;
  status: "started" | "complete" | "error";
  label?: string;
}

export interface PenaltyExposureProgressContext {
  includeCon?: boolean;
  includeEnforcementHistory?: boolean;
}

const VERTICALS_WITHOUT_CERTIFICATE_OF_NEED = new Set([
  "aviation",
  "food_service",
  "pharma_manufacturing",
]);

const FACILITY_TYPES_WITHOUT_CERTIFICATE_OF_NEED = new Set([
  "pharma_finished_dose_manufacturer",
  "pharma_api_manufacturer",
  "pharma_contract_manufacturer",
  "pharma_repackager",
  "pharma_wholesale_distributor",
  "pharma_third_party_logistics",
]);

const PARALLEL_STEP_DEFS: { stepId: string; label: string; when?: keyof PenaltyExposureProgressContext }[] = [
  { stepId: "mandatory_calendar", label: "Fetching mandatory compliance calendar" },
  { stepId: "licensing", label: "Analyzing licensing requirements" },
  { stepId: "accreditation", label: "Analyzing accreditation requirements" },
  { stepId: "con", label: "Analyzing certificate of need", when: "includeCon" },
  { stepId: "compliance_comparison", label: "Reviewing compliance deadlines" },
  { stepId: "audit_checklist", label: "Building audit-ready checklist" },
];

/** Penalties stream: only steps that may exceed ~1s (service fetch, enforcement ingest, LLM). */
const PENALTIES_STEP_DEFS: { stepId: string; label: string; when?: keyof PenaltyExposureProgressContext }[] = [
  { stepId: "penalties_cache", label: "Checking penalty cache" },
  { stepId: "enforcement_history", label: "Loading enforcement history", when: "includeEnforcementHistory" },
  { stepId: "mandatory_calendar", label: "Fetching mandatory compliance calendar" },
  { stepId: "penalty_analysis", label: "Analyzing potential penalties" },
];

/** Stable display order for every known step (cache first, then parallel, then penalties). */
const ALL_STEP_IDS_IN_ORDER: string[] = (() => {
  const seen = new Set<string>();
  const ids: string[] = [];
  const cacheFirst = PENALTIES_STEP_DEFS.filter((d) => d.stepId === "penalties_cache");
  const rest = [...PARALLEL_STEP_DEFS, ...PENALTIES_STEP_DEFS].filter(
    (d) => d.stepId !== "penalties_cache",
  );
  for (const def of [...cacheFirst, ...rest]) {
    if (seen.has(def.stepId)) continue;
    seen.add(def.stepId);
    ids.push(def.stepId);
  }
  return ids;
})();

export interface PenaltyExposureProgressFacilityContext {
  countryCode?: string | null;
  vertical?: string | null;
  facilityTypeSlug?: string | null;
  facilityType?: string | null;
}

/** Mirrors backend ``_certificate_of_need_applies`` for progress checklist visibility. */
export function certificateOfNeedAppliesForProgress(
  params: PenaltyExposureProgressFacilityContext,
): boolean {
  const country = String(params.countryCode || "USA").trim().toUpperCase();
  if (country !== "USA" && country !== "US") {
    return false;
  }
  const vertical = String(params.vertical || "healthcare").trim().toLowerCase();
  if (VERTICALS_WITHOUT_CERTIFICATE_OF_NEED.has(vertical)) {
    return false;
  }
  const facilityType = String(
    params.facilityTypeSlug || params.facilityType || "",
  )
    .trim()
    .toLowerCase();
  if (facilityType && FACILITY_TYPES_WITHOUT_CERTIFICATE_OF_NEED.has(facilityType)) {
    return false;
  }
  return true;
}

export function buildPenaltyExposureProgressContext(
  params: PenaltyExposureProgressFacilityContext,
): PenaltyExposureProgressContext {
  const country = String(params.countryCode || "USA").trim().toUpperCase();
  return {
    includeEnforcementHistory: country === "USA" || country === "US",
    includeCon: certificateOfNeedAppliesForProgress(params),
  };
}

function defsForContext(
  defs: { stepId: string; label: string; when?: keyof PenaltyExposureProgressContext }[],
  context: PenaltyExposureProgressContext,
): PenaltyExposureProgressStep[] {
  return defs
    .filter((def) => {
      if (!def.when) return true;
      return context[def.when] === true;
    })
    .map((def) => ({
      stepId: def.stepId,
      label: def.label,
      status: "queued" as const,
    }));
}

export function buildInitialPenaltyExposureProgressSteps(
  context: PenaltyExposureProgressContext = {},
): PenaltyExposureProgressStep[] {
  const parallel = defsForContext(PARALLEL_STEP_DEFS, context);
  const penalties = defsForContext(PENALTIES_STEP_DEFS, context);
  const seen = new Set<string>();
  const merged: PenaltyExposureProgressStep[] = [];
  const cacheStep = penalties.find((s) => s.stepId === "penalties_cache");
  if (cacheStep) {
    merged.push(cacheStep);
    seen.add(cacheStep.stepId);
  }
  for (const step of [...parallel, ...penalties]) {
    if (seen.has(step.stepId)) continue;
    seen.add(step.stepId);
    merged.push(step);
  }
  return merged;
}

function insertStepInCanonicalOrder(
  steps: PenaltyExposureProgressStep[],
  newStep: PenaltyExposureProgressStep,
): PenaltyExposureProgressStep[] {
  const orderIndex = ALL_STEP_IDS_IN_ORDER.indexOf(newStep.stepId);
  if (orderIndex < 0) {
    return [...steps, newStep];
  }
  let insertAt = steps.length;
  for (let i = 0; i < steps.length; i++) {
    const existingIndex = ALL_STEP_IDS_IN_ORDER.indexOf(steps[i].stepId);
    if (existingIndex > orderIndex) {
      insertAt = i;
      break;
    }
  }
  return [...steps.slice(0, insertAt), newStep, ...steps.slice(insertAt)];
}

function upsertStep(
  steps: PenaltyExposureProgressStep[],
  event: ComplianceProgressEvent,
): PenaltyExposureProgressStep[] {
  const label = event.label?.trim();
  const idx = steps.findIndex((s) => s.stepId === event.stepId);
  const nextStatus: PenaltyExposureProgressStatus =
    event.status === "started"
      ? "active"
      : event.status === "error"
        ? "error"
        : "complete";

  if (idx >= 0) {
    return steps.map((step, i) =>
      i === idx
        ? {
            ...step,
            label: label || step.label,
            status: nextStatus,
          }
        : step,
    );
  }

  return [
    ...insertStepInCanonicalOrder(steps, {
      stepId: event.stepId,
      label: label || event.stepId.replace(/_/g, " "),
      status: nextStatus,
    }),
  ];
}

export function applyComplianceProgressEvent(
  steps: PenaltyExposureProgressStep[],
  event: ComplianceProgressEvent,
): PenaltyExposureProgressStep[] {
  return upsertStep(steps, event);
}

export function markPenaltyExposureProgressStep(
  steps: PenaltyExposureProgressStep[],
  stepId: string,
  status: PenaltyExposureProgressStatus,
): PenaltyExposureProgressStep[] {
  const idx = steps.findIndex((s) => s.stepId === stepId);
  if (idx < 0) return steps;
  return steps.map((step, i) => (i === idx ? { ...step, status } : step));
}

const ANALYSIS_PROGRESS_STEP_IDS = [
  "mandatory_calendar",
  "licensing",
  "accreditation",
  "con",
  "compliance_comparison",
  "audit_checklist",
] as const;

const PENALTIES_PROGRESS_STEP_IDS = [
  "penalties_cache",
  "enforcement_history",
  "mandatory_calendar",
  "penalty_analysis",
] as const;

/** Mark an SSE block complete when cache replay finishes or the stream ends. */
export function markPenaltyExposureProgressBlockComplete(
  steps: PenaltyExposureProgressStep[],
  block: "analysis" | "penalties",
): PenaltyExposureProgressStep[] {
  const ids =
    block === "analysis" ? ANALYSIS_PROGRESS_STEP_IDS : PENALTIES_PROGRESS_STEP_IDS;
  const idSet = new Set<string>(ids);
  return steps.map((step) =>
    idSet.has(step.stepId) && step.status !== "complete" && step.status !== "error"
      ? { ...step, status: "complete" as const }
      : step,
  );
}

export function allPenaltyExposureProgressStepsComplete(
  steps: PenaltyExposureProgressStep[],
): boolean {
  return steps.length > 0 && steps.every((s) => s.status === "complete");
}

export const PENALTY_EXPOSURE_COUNTDOWN_INITIAL = 10;

/** Countdown shown at the top of the progress list; decrements once per completed step. */
export function computePenaltyExposureCountdown(
  steps: readonly PenaltyExposureProgressStep[],
  initial: number = PENALTY_EXPOSURE_COUNTDOWN_INITIAL,
): number {
  if (allPenaltyExposureProgressStepsComplete([...steps])) {
    return 0;
  }
  const completed = steps.filter((s) => s.status === "complete").length;
  return Math.max(0, initial - completed);
}
